import { Modal } from '../ui/Modal.jsx';
import { Badge } from '../ui/Badge.jsx';
import { Copyable } from '../ui/Copyable.jsx';
import { rentalsApi } from '../../api/rentals.js';
import { formatDate } from '../../utils/dates.js';

function formatRon(v) {
  if (v == null) return '—';
  return `${Number(v).toLocaleString('ro-RO')} RON`;
}

function MoneyRow({ label, value, highlight }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-sm text-muted">{label}</span>
      <span className={`font-mono text-sm ${highlight ? 'font-semibold text-ink' : 'text-ink'}`}>
        {formatRon(value)}
      </span>
    </div>
  );
}

export function RentalDetailModal({ open, onClose, rental }) {
  if (!rental) return null;

  // Calcul: de plată la retur = cost - avans. Doar dacă ambele sunt setate.
  const ramasDeIncasat =
    rental.cost_contract != null && rental.avans_primit != null
      ? rental.cost_contract - rental.avans_primit
      : null;

  const hasMoneyInfo =
    rental.cost_contract != null || rental.garantie != null || rental.avans_primit != null;

  return (
    <Modal open={open} onClose={onClose} title={null}>
      <div className="space-y-4">
        {/* Header nume + status */}
        <div className="flex items-start justify-between gap-2">
          <div>
            <Copyable value={rental.chirias_nume} as="button" className="text-xl font-semibold text-ink hover:text-teal-bright">
              {rental.chirias_nume}
            </Copyable>
            <div className="mt-1">
              {rental.activ ? <Badge tone="ok">Activă</Badge> : <Badge tone="muted">Închisă</Badge>}
            </div>
          </div>
          <button onClick={onClose} className="rounded-md p-1 text-muted hover:bg-cream" aria-label="Închide">
            ✕
          </button>
        </div>

        {/* Contact */}
        {rental.chirias_telefon && (
          <div className="card bg-cream/60">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted">Telefon</div>
            <div className="mt-1 flex items-center gap-3">
              <Copyable value={rental.chirias_telefon} as="button" className="font-mono text-base text-ink hover:text-teal-bright">
                {rental.chirias_telefon}
              </Copyable>
              <a href={`tel:${rental.chirias_telefon}`} className="text-xs text-teal-bright underline">
                Sună
              </a>
            </div>
          </div>
        )}

        {/* Perioadă + KM */}
        <div className="card">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted mb-2">Perioadă</div>
          <div className="font-mono text-sm text-ink">
            {formatDate(rental.data_start)} → {rental.data_end ? formatDate(rental.data_end) : 'în curs'}
          </div>
          {(rental.km_start != null || rental.km_end != null) && (
            <div className="mt-2">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted mb-1">Kilometraj</div>
              <div className="font-mono text-sm">
                {rental.km_start != null ? `${rental.km_start.toLocaleString('ro-RO')} km` : '—'}
                {' → '}
                {rental.km_end != null ? `${rental.km_end.toLocaleString('ro-RO')} km` : '(la retur)'}
                {rental.km_start != null && rental.km_end != null && (
                  <span className="ml-2 text-xs text-muted">
                    (rulat {Math.max(0, rental.km_end - rental.km_start).toLocaleString('ro-RO')} km)
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Contract */}
        {hasMoneyInfo && (
          <div className="card">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted mb-2">Contract</div>
            <MoneyRow label="Cost contract" value={rental.cost_contract} />
            <MoneyRow label="Garanție" value={rental.garantie} />
            <MoneyRow label="Avans primit" value={rental.avans_primit} />
            {ramasDeIncasat != null && (
              <div className="mt-1 border-t border-border pt-2">
                <MoneyRow
                  label={ramasDeIncasat > 0 ? 'De încasat la retur' : ramasDeIncasat < 0 ? 'De restituit' : 'Achitat integral'}
                  value={Math.abs(ramasDeIncasat)}
                  highlight
                />
              </div>
            )}
          </div>
        )}

        {/* Documente + contract */}
        {(rental.id_document_path || rental.permis_document_path || rental.contract_path) && (
          <div>
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted">Documente</div>
            <div className="space-y-2">
              {rental.contract_path && (
                <a
                  href={rentalsApi.fileUrl(rental.id, 'contract')}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-between gap-2 rounded-lg border border-ok/30 bg-ok/10 px-3 py-3 font-semibold text-ok hover:bg-ok/20"
                >
                  <span className="flex items-center gap-2">
                    <span aria-hidden>✓</span> Contract semnat (PDF)
                  </span>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                    <polyline points="15 3 21 3 21 9"/>
                    <line x1="10" y1="14" x2="21" y2="3"/>
                  </svg>
                </a>
              )}
              {rental.id_document_path && (
                <a
                  href={rentalsApi.fileUrl(rental.id, 'id')}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-between gap-2 rounded-lg border border-teal/20 bg-teal/5 px-3 py-3 font-semibold text-teal-bright hover:bg-teal/10"
                >
                  <span className="flex items-center gap-2">
                    <span aria-hidden>📄</span> CI / Carte identitate
                  </span>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                    <polyline points="15 3 21 3 21 9"/>
                    <line x1="10" y1="14" x2="21" y2="3"/>
                  </svg>
                </a>
              )}
              {rental.permis_document_path && (
                <a
                  href={rentalsApi.fileUrl(rental.id, 'permis')}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-between gap-2 rounded-lg border border-teal/20 bg-teal/5 px-3 py-3 font-semibold text-teal-bright hover:bg-teal/10"
                >
                  <span className="flex items-center gap-2">
                    <span aria-hidden>📄</span> Permis conducere
                  </span>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                    <polyline points="15 3 21 3 21 9"/>
                    <line x1="10" y1="14" x2="21" y2="3"/>
                  </svg>
                </a>
              )}
            </div>
          </div>
        )}

        {/* Observații */}
        {rental.observatii && (
          <div className="card bg-cream/60">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted mb-1">Observații</div>
            <p className="text-sm text-ink whitespace-pre-line">{rental.observatii}</p>
          </div>
        )}

        <div className="flex justify-end">
          <button onClick={onClose} className="btn-ghost border border-border">Închide</button>
        </div>
      </div>
    </Modal>
  );
}
